import React, { useState } from 'react'
import { gql, useMutation } from '@apollo/client'

import { useRouteMatch } from 'react-router-dom'
import { makeStyles } from '@material-ui/styles'
import { Box, Grid, Tooltip, Typography } from '@material-ui/core'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import EventAvailableIcon from '@material-ui/icons/EventAvailable'
import VisibilityIcon from '@material-ui/icons/Visibility'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'
import { useSnackbar } from 'notistack'

import RouterLinkButton from 'components/router-link-button'

const TOGGLE_COACH_PROFILE_FACILITY_VISIBILITY = gql`
  mutation ToggleCoachProfileFacilityVisibility(
    $params: ToggleCoachProfileFacilityVisibilityInput!
  ) {
    ToggleCoachProfileFacilityVisibility(params: $params) {
      message
      success
    }
  }
`

const useStyles = makeStyles(theme => ({
  facilityWrapper: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(3),
    alignItems: 'center',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '4px',
  },
  facilityTitle: {
    fontWeight: '500',
    fontSize: '16px',
  },
  facilityTitleWrapper: {
    display: 'flex',
  },
  facilityIcons: {
    paddingLeft: theme.spacing(3),
  },
  facilityIcon: {
    marginRight: theme.spacing(2),
    display: 'inline',
  },
  togglePointer: {
    cursor: 'pointer',
  },
  facilityArrow: {
    float: 'right',
  },
}))

const FacilityListItem = ({
  facility,
  index,
  hasAvailabilities,
  hasCoachProfile,
}) => {
  const styles = useStyles()
  const { path } = useRouteMatch()
  const { enqueueSnackbar } = useSnackbar()

  const [isVisible, setIsVisible] = useState(
    facility.coachProfilesFacilities[0]?.visible,
  )
  const [updateVisibility] = useMutation(
    TOGGLE_COACH_PROFILE_FACILITY_VISIBILITY,
  )

  const handleVisibilityClick = () => {
    updateVisibility({
      variables: {
        params: {
          facilityId: facility.id,
          visible: !isVisible,
        },
      },
    })
      .then(data => {
        if (!data?.data?.ToggleCoachProfileFacilityVisibility?.success) {
          enqueueSnackbar(
            data?.ToggleCoachProfileFacilityVisibility?.message ||
              'Something went wrong',
            {
              variant: 'error',
            },
          )
        } else {
          setIsVisible(!isVisible)
        }
      })
      .catch(err => {
        enqueueSnackbar(err?.message || 'Something went wrong', {
          variant: 'error',
        })
      })
  }

  const visibilityToggles = () => {
    if (hasCoachProfile) {
      return isVisible ? (
        <Tooltip title="Hide this facility on your profile" placement="top">
          <VisibilityIcon
            className={styles.togglePointer}
            onClick={() => handleVisibilityClick()}
          />
        </Tooltip>
      ) : (
        <Tooltip title="Show this facility on your profile" placement="top">
          <VisibilityOffIcon
            className={styles.togglePointer}
            onClick={() => handleVisibilityClick()}
          />
        </Tooltip>
      )
    } else {
      return null
    }
  }

  return (
    <Grid container className={styles.facilityWrapper} key={index}>
      <Grid item className={styles.facilityTitleWrapper} xs={5}>
        <Typography className={styles.facilityTitle}>
          {facility.name}
        </Typography>
      </Grid>
      <Grid item className={styles.facilityChips} xs={6}>
        <Box className={styles.facilityIcons}>
          <Box className={styles.facilityIcon}>
            {hasAvailabilities ? (
              <Tooltip title="This facility can be booked by your students.">
                <EventAvailableIcon />
              </Tooltip>
            ) : (
              <Tooltip title="This facility cannot be booked by your students.">
                <CalendarTodayIcon />
              </Tooltip>
            )}
          </Box>
          <Box className={`${styles.facilityIcon} ${styles.togglePointer}`}>
            {visibilityToggles}
          </Box>
        </Box>
      </Grid>
      <Grid item xs={1}>
        <RouterLinkButton
          to={{
            pathname: `${path}/${facility.id}`,
            state: { facility: facility },
          }}
          className={styles.facilityArrow}
          data-cy="facilityShowLink"
        >
          <ChevronRightIcon />
        </RouterLinkButton>
      </Grid>
    </Grid>
  )
}

export default FacilityListItem
